import { logger } from '@utils/safeLogger'
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ITwilioPortability,
  PortRequestChannel,
  PortRequestProvider,
  PortRequestReviewStatus,
  PortRequestSourceModule,
  PortRequestStatus,
} from '@dataTypes/twilio.types'
import axiosOriginal from 'axios'
import axios from './axiosConfig'
import {
  IClinic,
  IClinicCreateRequest,
  IClinicUpdateRequest,
  IClinicBulkDeleteRequest,
  ITwilioPortDocument,
  ITwilioPortRequest,
  ICreatePortInResponse,
} from '@dataTypes/clinic.type'

import { getEnv } from '@utils/env'
import { getAccessToken } from '@services/auth/accessTokenStore'
import { toApiError } from './errors/api-error'

// Constants
const BUSINESS_LAYER_API = getEnv(
  'REACT_APP_BUSINESS_LAYER_CLINIC',
  // 'http://localhost:4003',
)
const EXTERNAL_LAYER_API = getEnv(
  'REACT_APP_EXTERNAL_LAYER_CLINIC',
  'http://localhost:5007',
)

const headers = {
  'Content-Type': 'application/json',
}

// Types
interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

type ClinicsResponseData = {
  clinics?: IClinic[]
}

type ClinicResponseData = {
  clinic?: IClinic
}

type ConnectToTwilioResponseData = {
  authorize_url: string
}

type CheckPortabilityResponseData = {
  portability: ITwilioPortability[]
}

type GenerateUploadUrlResponseData = {
  upload_url: string
  blob_url?: string
  blob_name?: string
}

type GenerateReadOnlyUrlResponseData = {
  read_only_url: string
}

type DeleteBlobPrefixResponseData = {
  deleted_count: number
}

type UploadPortingDocumentResponseData = {
  document: {
    sid: string
    account_sid: string
    document_type: string
    version: number
    status: string
    date_created: Date
    date_updated: Date
    friendly_name: string
    mime_type: string
    failure_reason?: string
    created_by: string
  }
}

type CreatePortDocumentResponseData = {
  twilio_port_document: ITwilioPortDocument
}

type PortDocumentResponseData = {
  twilio_port_documents: ITwilioPortDocument[]
}

type CreatePortRequestResponseData = {
  twilio_port_request: ITwilioPortRequest
}

type PortRequestsResponseData = {
  twilio_port_requests: ITwilioPortRequest[]
}

type PortInRequestResponseData = {
  twilio_port_request: ICreatePortInResponse
}

type GetAllClinicsResponse = ApiResponse<ClinicsResponseData>
type GetClinicResponse = ApiResponse<ClinicResponseData>
type CreateClinicResponse = ApiResponse<Record<string, unknown>>
type UpdateClinicResponse = ApiResponse<ClinicResponseData>
type DeleteClinicResponse = ApiResponse<Record<string, unknown>>
type ConnectToTwilioResponse = ApiResponse<ConnectToTwilioResponseData>
type CheckPortabilityResponse = ApiResponse<CheckPortabilityResponseData>
type GenerateUploadUrlResponse = ApiResponse<GenerateUploadUrlResponseData>
type GenerateReadOnlyUrlResponse = ApiResponse<GenerateReadOnlyUrlResponseData>
type DeleteBlobPrefixResponse = ApiResponse<DeleteBlobPrefixResponseData>
type UploadPortingDocumentResponse =
  ApiResponse<UploadPortingDocumentResponseData>
type CreatePortDocumentResponse = ApiResponse<CreatePortDocumentResponseData>
type PortDocumentsResponse = ApiResponse<PortDocumentResponseData>
type CreatePortRequestResponse = ApiResponse<CreatePortRequestResponseData>
type PortRequestsResponse = ApiResponse<PortRequestsResponseData>
type PortInRequestResponse = ApiResponse<PortInRequestResponseData>
type GenericResponse = ApiResponse<Record<string, unknown>>

// API Functions
export const getAllClinics = async (options?: {
  accessToken?: string
}): Promise<GetAllClinicsResponse> => {
  try {
    const response = await axios.get<GetAllClinicsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic`,
      {
        headers: {
          ...headers,
          ...(options?.accessToken && {
            Authorization: `Bearer ${options.accessToken}`,
          }),
        },
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in getAllClinics:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getClinicById = async (id: string): Promise<GetClinicResponse> => {
  try {
    const response = await axios.get<GetClinicResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/${id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in getClinicById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createClinic = async (
  clinicData: IClinicCreateRequest,
): Promise<CreateClinicResponse> => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const response = await axios.post<CreateClinicResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic`,
      {
        ...clinicData,
        created_by: user.user_id,
        updated_by: user.user_id,
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in createClinic:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updateClinicById = async (
  clinic_id: string,
  updates: Partial<IClinic>,
): Promise<UpdateClinicResponse> => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}')
    const updateData: IClinicUpdateRequest = {
      clinic_id,
      data: {
        ...updates,
        updated_by: user.user_id,
      },
    }

    const response = await axios.put<UpdateClinicResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic`,
      updateData,
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in updateClinicById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const deleteClinicById = async (
  id: string,
): Promise<DeleteClinicResponse> => {
  try {
    const response = await axios.delete<DeleteClinicResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/${id}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in deleteClinicById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const bulkDeleteClinics = async (
  clinic_ids: string[],
): Promise<DeleteClinicResponse> => {
  try {
    const deleteData: IClinicBulkDeleteRequest = {
      clinic_ids,
    }

    const response = await axios.delete<DeleteClinicResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/bulk`,
      {
        headers,
        data: deleteData,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in bulkDeleteClinics:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const connectToTwilio = async (
  clinic_id: string,
): Promise<ConnectToTwilioResponse> => {
  try {
    const response = await axios.get<ConnectToTwilioResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/oauth-connect?clinic_id=${clinic_id}`,
    )
    return response.data
  } catch (e: any) {
    logger.error('Error in connectToTwilio:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const checkPhoneNumberPortability = async (
  phone_numbers: string[],
): Promise<CheckPortabilityResponse> => {
  try {
    const response = await axios.post<CheckPortabilityResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/check-portability`,
      phone_numbers,
      { headers },
    )
    return response.data
  } catch (e: any) {
    logger.error('Error in checkPortability:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const generateUploadUrl = async (
  contentType: string,
  filename: string,
  options?: {
    folder?: string
    clinic_id?: string
    extra_path?: string
  },
): Promise<GenerateUploadUrlResponse> => {
  try {
    const response = await axios.post<GenerateUploadUrlResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/create-upload-url`,
      {
        contentType,
        filename,
        ...(options?.folder ? { folder: options.folder } : {}),
        ...(options?.clinic_id ? { clinic_id: options.clinic_id } : {}),
        ...(options?.extra_path ? { extra_path: options.extra_path } : {}),
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in generateUploadUrl:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const generateReadOnlyUrl = async (
  blobRef: string,
  expirationHours?: number,
  clinicId?: string,
): Promise<GenerateReadOnlyUrlResponse> => {
  try {
    const response = await axios.post<GenerateReadOnlyUrlResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/generate-read-only-url`,
      {
        url: /^https?:\/\//i.test(blobRef) ? blobRef : undefined,
        filename: blobRef,
        ...(clinicId ? { clinic_id: clinicId } : {}),
        ...(expirationHours && { expirationHours }),
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in generateReadOnlyUrl:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const uploadFile = async (
  url: string,
  file: File,
): Promise<GenericResponse> => {
  try {
    const response = await axiosOriginal.put<
      ApiResponse<Record<string, unknown>>
    >(url, file, {
      headers: {
        'x-ms-blob-type': 'BlockBlob',
        'Content-Type': file.type,
      },
    })
    return response.data
  } catch (e: any) {
    logger.error('Error in uploadFile:', e)
    throw toApiError(e, 'Failed to upload file')
  }
}

export const deleteBlobPrefix = async (
  folder: string,
  extraPath?: string,
  clinicId?: string,
): Promise<DeleteBlobPrefixResponse> => {
  try {
    const response = await axios.post<DeleteBlobPrefixResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/delete-blob-prefix`,
      {
        folder,
        ...(extraPath ? { extra_path: extraPath } : {}),
        ...(clinicId ? { clinic_id: clinicId } : {}),
      },
    )
    return response.data
  } catch (e: any) {
    logger.error('Error in deleteBlobPrefix:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const uploadPortingDocumentToTwilio = async (
  file: File,
  filename: string,
  documentType: string,
): Promise<UploadPortingDocumentResponse> => {
  try {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('filename', filename)
    formData.append('document_type', documentType)

    const response = await axios.post<UploadPortingDocumentResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/upload-porting-document`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in uploadPortingDocumentToTwilio:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createPortDocument = async (
  clinicId: string,
  documentSid: string,
  documentType: string,
  fileUrl: string,
): Promise<CreatePortDocumentResponse> => {
  try {
    const response = await axios.post<CreatePortDocumentResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/port-document`,
      {
        clinic_id: clinicId,
        document_sid: documentSid,
        document_type: documentType,
        file_url: fileUrl,
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in createPortDocument:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getPortDocuments = async (
  clinicId?: string,
): Promise<PortDocumentsResponse> => {
  try {
    const params = clinicId ? { clinic_id: clinicId } : {}
    const response = await axios.get<PortDocumentsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/port-document`,
      {
        headers,
        params,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in getPortDocuments:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createTwilioPortRequest = async (
  phoneNumber: string,
  fileUrls: string[],
  adminEmail: string,
  userFullname: string,
  userEmail: string,
  companyCustomerName: string,
  customerType?: 'Individual' | 'Business',
): Promise<PortInRequestResponse> => {
  try {
    const response = await axios.post<PortInRequestResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/create-port-request`,
      {
        data: {
          phone_number: phoneNumber,
          file_urls: fileUrls,
          admin_email: adminEmail,
          user_fullname: userFullname,
          user_email: userEmail,
          company_customer_name: companyCustomerName,
          ...(customerType && { customer_type: customerType }),
        },
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in createTwilioPortRequest:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const createPortRequest = async (payload: {
  clinic_id: string
  phone_numbers: string[]
  phone_carrier: string
  twilio_port_document_id: string | null
  twilio_request_sid?: string | null
  representative_name: string
  representative_email: string
  wireless_phone_number?: string | null
  wireless_account_number?: string | null
  wireless_pin_or_ssn?: string | null
  status: PortRequestStatus
  twilio_status: string
  target_port_date: string
  channel?: PortRequestChannel
  provider?: PortRequestProvider
  source_module?: PortRequestSourceModule
  review_status?: PortRequestReviewStatus
  requested_by_user_id?: string | null
  requested_by_name?: string | null
  provider_payload?: Record<string, unknown> | null
}): Promise<CreatePortRequestResponse> => {
  try {
    const response = await axios.post<CreatePortRequestResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/port-request`,
      payload,
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in createPortRequest:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getAllPortRequests = async (query?: {
  review_status?: string
  is_latest?: boolean
}): Promise<PortRequestsResponse> => {
  try {
    const token = getAccessToken() || ''
    if (!token) {
      throw toApiError(
        new Error('Authentication failed: access token missing'),
        'Authentication failed: access token missing',
      )
    }

    const params: Record<string, string> = {}
    if (query?.review_status) {
      params.review_status = query.review_status
    }
    if (query?.is_latest !== undefined) {
      params.is_latest = String(query.is_latest)
    }

    const response = await axios.get<PortRequestsResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/port-request`,
      {
        headers,
        params: Object.keys(params).length > 0 ? params : undefined,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in getAllPortRequests:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getPortRequestById = async (
  portRequestId: string,
): Promise<CreatePortRequestResponse> => {
  try {
    const response = await axios.get<CreatePortRequestResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/port-request/${portRequestId}`,
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in getPortRequestById:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const getPortRequestStatusFromTwilio = async (
  portInRequestSid: string,
): Promise<PortInRequestResponse> => {
  try {
    const response = await axios.get<PortInRequestResponse>(
      `${EXTERNAL_LAYER_API}/api/v1/external/clinic/port-request-status/${portInRequestSid}`,
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in getPortRequestStatusFromTwilio:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updatePortRequest = async (
  portRequestId: string,
  data: Partial<ITwilioPortRequest>,
): Promise<CreatePortRequestResponse> => {
  try {
    const response = await axios.put<CreatePortRequestResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/port-request`,
      {
        twilio_port_request_id: portRequestId,
        data,
      },
      {
        headers,
      },
    )

    return response.data
  } catch (e: any) {
    logger.error('Error in updatePortRequest:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}

export const updatePortRequestStatus = async (
  portRequestId: string,
  status: string,
  rejectionReason?: string,
): Promise<CreatePortRequestResponse> => {
  return updatePortRequest(portRequestId, {
    status,
    ...(rejectionReason ? { rejection_reason: rejectionReason } : {}),
  })
}

export const resubmitPortRequest = async (
  portRequestId: string,
  data: Partial<ITwilioPortRequest>,
): Promise<CreatePortRequestResponse> => {
  try {
    const response = await axios.post<CreatePortRequestResponse>(
      `${BUSINESS_LAYER_API}/api/v1/clinic/port-request/${portRequestId}/resubmit`,
      { data },
      { headers },
    )
    return response.data
  } catch (e: any) {
    logger.error('Error in resubmitPortRequest:', e)
    throw toApiError(e, 'An unknown error occurred')
  }
}
